import { useMemo } from "react"
import { keepPreviousData } from "@tanstack/react-query"

import { useVariantMap } from "~/hooks/useVariants"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { api } from "~/utils/api"
import { parseStoreSkuKey, QTY_FIELDS, SETTING_FIELDS } from "~/utils/allocationLineSettings"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { showErrorToast } from "~/utils/toaster"
import {
  useAllocationPackages,
  useAllocationStores,
  type StoreWithStatus,
} from "../../useAllocationData"
import type { RangePackage } from "../../types"
import type { DraftChange } from "../../DraftCommandbar"
import { noteRunAfterCurrent, useSetupDrafts } from "../setupDrafts"
import {
  EMPTY_PRODUCT_SETTINGS_DRAFT,
  draftSaveBlocker,
  draftToPatch,
  mergeSettingsReads,
  productListState,
  variantIdBatches,
  type ListedVariant,
  type ProductSettingsDraft,
  type SavedProductSettings,
} from "./productSettingsModel"
import { SETTING_FIELD_META, showSettingValue } from "./productSettingFields"

const ONE_SIZE = "One size"

/** A variant as Settings › Products lists it: at every store, over all its packages, or within one. */
export type ProductListing = ListedVariant & {
  /** The variant id over all its packages; "packageId/variantId" within one package; see everywhereScopeId and storeScopeId. */
  scopeId: string
  name: string
  productId: string
  productName: string
  variantName: string
  imageUrl: string | null
  category: string | null
  color: string | null
  packageIds: string[]
  /** AllocationStore ids the listed sizes reach, in store name order. */
  storeIds: readonly string[]
}

export const packageScopeId = (packageId: string, variantId: string) => `${packageId}/${variantId}`
/** Every store's listing of a variant has its own scope id, apart from its package listing's. */
export const everywhereScopeId = (variantId: string) => `everywhere/${variantId}`

/** The variant's own name; some catalogues repeat the product name in front of it. */
function variantLabel(productName?: string | null, variantName?: string | null) {
  const name = variantName ?? ""
  if (!productName || !name.startsWith(productName)) return name
  return name.slice(productName.length).trim() || name
}

/** The stores each size is sent to. */
type Reach = Map<string, Set<string>>
type VariantReach = {
  packageIds: string[]
  reach: Reach
  perPackage: { packageId: string; reach: Reach }[]
}

/**
 * `everywhere`: every variant in the catalogue, each size at every store, so any store x SKU can be
 * set whether a package sends it or not. `listings`: one per variant in any package, over every
 * package that carries it; a size reaches the stores of the packages that carry that size.
 * `scoped`: the same within one package, only for variants in several packages (the others read
 * from `listings`), by packageScopeId.
 */
export function buildListings(
  packages: Pick<RangePackage, "id" | "productIds" | "skuIds" | "storeIds">[],
  stores: Pick<StoreWithStatus, "id" | "allocationLocationId">[],
  variantMap: ReadonlyMap<string, IVariantSummary>,
): {
  everywhere: Map<string, ProductListing>
  listings: Map<string, ProductListing>
  scoped: Map<string, ProductListing>
} {
  const storeOrder = new Map(stores.map((store, index) => [store.id, index]))
  const locationOf = new Map(stores.map((store) => [store.id, store.allocationLocationId]))
  const byVariant = new Map<string, VariantReach>()
  for (const pkg of packages) {
    const pkgSkus = new Set(pkg.skuIds)
    const pkgStores = pkg.storeIds.filter((storeId) => locationOf.has(storeId))
    for (const variantId of pkg.productIds) {
      const entry: VariantReach = byVariant.get(variantId) ?? {
        packageIds: [],
        reach: new Map(),
        perPackage: [],
      }
      const reach: Reach = new Map()
      for (const sku of variantMap.get(variantId)?.skuData ?? []) {
        if (!pkgSkus.has(sku.skuId)) continue
        reach.set(sku.skuId, new Set(pkgStores))
        const all = entry.reach.get(sku.skuId) ?? new Set<string>()
        for (const storeId of pkgStores) all.add(storeId)
        entry.reach.set(sku.skuId, all)
      }
      entry.packageIds.push(pkg.id)
      entry.perPackage.push({ packageId: pkg.id, reach })
      byVariant.set(variantId, entry)
    }
  }
  const inStoreOrder = (storeIds: Iterable<string>) =>
    [...storeIds].sort((a, b) => (storeOrder.get(a) ?? 0) - (storeOrder.get(b) ?? 0))
  /** `locationsOf` gives a size's locations, or undefined when the listing leaves the size out. */
  const listingOf = (
    variantId: string,
    scopeId: string,
    packageIds: string[],
    locationsOf: (skuId: string) => readonly string[] | undefined,
    storeIds: readonly string[],
  ): ProductListing => {
    const variant = variantMap.get(variantId)
    return {
      variantId,
      scopeId,
      name: variant?.variantDisplayName ?? variantId,
      productId: variant?.productId ?? variantId,
      productName: variant?.productName ?? variantId,
      variantName: variantLabel(variant?.productName, variant?.variantName),
      imageUrl: variant?.imageUrl ?? null,
      category: variant?.category ?? null,
      color: variant?.color ?? null,
      packageIds,
      skus: (variant?.skuData ?? [])
        .flatMap((sku) => {
          const locationIds = locationsOf(sku.skuId)
          return locationIds
            ? [{ skuId: sku.skuId, size: sku.size?.trim() || ONE_SIZE, locationIds }]
            : []
        })
        .sort((a, b) => sortSizesComparator(a.size, b.size)),
      storeIds,
    }
  }
  const reachListing = (variantId: string, scopeId: string, packageIds: string[], reach: Reach) =>
    listingOf(
      variantId,
      scopeId,
      packageIds,
      (skuId) => {
        const stores = reach.get(skuId)
        return stores && inStoreOrder(stores).map((id) => locationOf.get(id)!)
      },
      inStoreOrder(new Set([...reach.values()].flatMap((set) => [...set]))),
    )

  const listings = new Map<string, ProductListing>()
  const scoped = new Map<string, ProductListing>()
  for (const [variantId, entry] of byVariant) {
    listings.set(variantId, reachListing(variantId, variantId, entry.packageIds, entry.reach))
    if (entry.perPackage.length < 2) continue
    for (const { packageId, reach } of entry.perPackage) {
      const scopeId = packageScopeId(packageId, variantId)
      scoped.set(scopeId, reachListing(variantId, scopeId, [packageId], reach))
    }
  }
  const allStoreIds = inStoreOrder(locationOf.keys())
  const allLocationIds = allStoreIds.map((id) => locationOf.get(id)!)
  const everywhere = new Map<string, ProductListing>()
  for (const variantId of new Set([...variantMap.keys(), ...byVariant.keys()])) {
    const packageIds = byVariant.get(variantId)?.packageIds ?? []
    everywhere.set(
      variantId,
      listingOf(
        variantId,
        everywhereScopeId(variantId),
        packageIds,
        () => allLocationIds,
        allStoreIds,
      ),
    )
  }
  return { everywhere, listings, scoped }
}

type SettingsResult = {
  data?: Parameters<typeof mergeSettingsReads>[0][number]
  isError: boolean
}

// Module-level, so useQueries only recombines when a batch's result changes.
export function combineSettingsReads(results: SettingsResult[]) {
  return {
    saved: mergeSettingsReads(results.map((result) => result.data)),
    isError: results.some((result) => result.isError),
  }
}

/**
 * Packages, stores, the listed variants and their saved settings, read in batches of at most 5,000
 * variants: the packaged and drafted ones, and with `allVariants` the whole catalogue.
 */
export function useProductSettingsData(allVariants: boolean) {
  const { packages, isLoading: packagesLoading } = useAllocationPackages()
  const { stores, isLoading: storesLoading } = useAllocationStores()
  const variantMap = useVariantMap()
  const { productSettingsDraft: draft } = useSetupDrafts()
  const draftedIds = Object.keys(draft).sort().join(",")
  const batches = useMemo(
    () =>
      variantIdBatches([
        ...packages.flatMap((pkg) => pkg.productIds),
        ...(draftedIds === "" ? [] : draftedIds.split(",")),
        ...(allVariants ? variantMap.keys() : []),
      ]),
    [packages, draftedIds, allVariants, variantMap],
  )
  const settings = api.useQueries(
    (t) =>
      batches.map((variantIds) =>
        // A package change re-batches; the old values stay up until the new batches arrive.
        t.allocation.getProductSettings({ variantIds }, { placeholderData: keepPreviousData }),
      ),
    { combine: combineSettingsReads },
  )
  const { everywhere, listings, scoped } = useMemo(
    () => buildListings(packages, stores, variantMap),
    [packages, stores, variantMap],
  )
  const { isLoading, emptyMessage } = productListState({
    basicsLoading: packagesLoading || storesLoading,
    batchCount: batches.length,
    saved: settings.saved,
    isError: settings.isError,
  })
  return {
    packages,
    stores,
    everywhere,
    listings,
    scoped,
    batches,
    saved: settings.saved,
    emptyMessage,
    isLoading,
  }
}

type ChangeNames = {
  variantName: (variantId: string) => string
  skuLabel: (skuId: string) => string
  storeName: (locationId: string) => string
}

/** Details rows: one per staged field, saved value before, staged value after; variant rows first. */
export function productSettingsChanges(
  draft: ProductSettingsDraft,
  saved: SavedProductSettings,
  names: ChangeNames,
): DraftChange[] {
  const entries = Object.entries(draft)
  const variantRows = entries.flatMap(([variantId, { variant }]) =>
    QTY_FIELDS.flatMap((field) => {
      if (variant?.[field] === undefined) return []
      return [
        {
          key: `variant:${variantId}:${field}`,
          label: `${names.variantName(variantId)} · All stores · ${SETTING_FIELD_META[field].label}`,
          before: showSettingValue(field, saved.variants.get(variantId)?.[field]),
          after: showSettingValue(field, variant[field]),
        },
      ]
    }),
  )
  const storeSkuRows = entries.flatMap(([, { cells }]) =>
    Object.entries(cells).flatMap(([cellKey, patch]) => {
      const { skuId, locationId } = parseStoreSkuKey(cellKey)
      return SETTING_FIELDS.flatMap((field) => {
        if (patch[field] === undefined) return []
        return [
          {
            key: `cell:${cellKey}:${field}`,
            label: `${names.skuLabel(skuId)} · ${names.storeName(locationId)} · ${SETTING_FIELD_META[field].label}`,
            before: showSettingValue(field, saved.storeSkus.get(cellKey)?.[field]),
            after: showSettingValue(field, patch[field]),
          },
        ]
      })
    }),
  )
  return [...variantRows, ...storeSkuRows]
}

/** The staged product settings, saved in one atomic call from the page Commandbar. */
export function useProductSettingsDraft(saved: SavedProductSettings | null, batches: string[][]) {
  const { productSettingsDraft: draft, setProductSettingsDraft: setDraft } = useSetupDrafts()
  const utils = api.useUtils()
  const mutation = api.allocation.saveProductSettings.useMutation({
    // The variants the draft touches, as it stands when Save is pressed.
    onMutate: () => ({ touched: new Set(Object.keys(draft)) }),
    // Awaited, so Save stays busy until the saved values are read back and the draft can go.
    onSuccess: (_result, _input, { touched }) =>
      Promise.all([
        ...batches
          .filter((variantIds) => variantIds.some((id) => touched.has(id)))
          .map((variantIds) => utils.allocation.getProductSettings.invalidate({ variantIds })),
        utils.allocation.getProductSettingsChangelog.invalidate(),
        utils.allocation.getRuleImpactLines.invalidate(),
        utils.allocation.getStoreTimeline.invalidate(),
      ]),
  })
  const saving = mutation.isPending

  return {
    draft,
    saving,
    saveBlocker: saved ? draftSaveBlocker(saved, draft) : "Loading the saved settings",
    update: (
      stage: (draft: ProductSettingsDraft, saved: SavedProductSettings) => ProductSettingsDraft,
    ) => {
      if (saving || !saved) return
      setDraft((prev) => stage(prev, saved))
    },
    discard: () => setDraft(EMPTY_PRODUCT_SETTINGS_DRAFT),
    save: () =>
      mutation.mutate(
        { ...draftToPatch(draft), source: "hand" },
        {
          onSuccess: (result) => {
            setDraft(EMPTY_PRODUCT_SETTINGS_DRAFT)
            noteRunAfterCurrent(result.run)
          },
          onError: showErrorToast,
        },
      ),
  }
}
